/**
 * Shape — an immutable polyomino defined by a set of cells.
 *
 * All cells are normalized on construction: translated so the
 * minimum row and column are 0, then sorted deterministically.
 * This makes equality comparison trivial via the `key` string.
 */

import { Cell, cellKey } from "./Cell";

function compareCells(a: Cell, b: Cell): number {
  return a.row !== b.row ? a.row - b.row : a.col - b.col;
}

function normalizeCells(raw: Cell[]): Cell[] {
  if (raw.length === 0) return [];
  const minR = Math.min(...raw.map((c) => c.row));
  const minC = Math.min(...raw.map((c) => c.col));
  return raw
    .map((c) => ({ row: c.row - minR, col: c.col - minC }))
    .sort(compareCells);
}

export class Shape {
  readonly cells: ReadonlyArray<Cell>;
  readonly key: string;

  constructor(cells: Cell[]) {
    this.cells = normalizeCells(cells);
    this.key = this.cells.map(cellKey).join("|");
  }

  /** Convenience factory from `[row, col]` tuples. */
  static from(...tuples: [number, number][]): Shape {
    return new Shape(tuples.map(([row, col]) => ({ row, col })));
  }

  get size(): number {
    return this.cells.length;
  }

  get width(): number {
    if (this.cells.length === 0) return 0;
    return Math.max(...this.cells.map((c) => c.col)) + 1;
  }

  get height(): number {
    if (this.cells.length === 0) return 0;
    return Math.max(...this.cells.map((c) => c.row)) + 1;
  }

  contains(row: number, col: number): boolean {
    return this.cells.some((c) => c.row === row && c.col === col);
  }

  /** 90-degree clockwise rotation: (r, c) → (c, -r). */
  rotate90(): Shape {
    return new Shape(this.cells.map((c) => ({ row: c.col, col: -c.row })));
  }

  /** All unique rotations (1–4 depending on symmetry). */
  allRotations(): Shape[] {
    const seen = new Set<string>();
    const result: Shape[] = [];
    let current: Shape = this;
    for (let i = 0; i < 4; i++) {
      if (!seen.has(current.key)) {
        seen.add(current.key);
        result.push(current);
      }
      current = current.rotate90();
    }
    return result;
  }

  /** Translate all cells by (dr, dc). */
  translate(dr: number, dc: number): Shape {
    return new Shape(this.cells.map((c) => ({ row: c.row + dr, col: c.col + dc })));
  }

  equals(other: Shape): boolean {
    return this.key === other.key;
  }
}
